/**
 * User flow constants for the entire application
 * Includes pre-onboarding, onboarding, and user state management
 */

// ============================================
// PRE-ONBOARDING FLOW (Registration process)
// ============================================

/**
 * Pre-onboarding step names that appear in user.stepName
 * These track the initial registration and setup flow
 */
export const PRE_ONBOARDING_STEPS = {
  // Legacy steps (customer-support flow) — kept for back-compat with existing
  // user.stepName data and the old pages, but NO LONGER part of the active
  // registration flow (Onboarding 2.0 removed questionnaire + avatar config).
  QUESTIONNAIRE: 'questionnaireStep',
  CONFIGURE_AVATAR: 'configureAvatarStep',
  PLATFORM_SELECTION: 'platformSelectionStep',
  // Onboarding 2.0 steps (Shopify-first, marketing-oriented). Each new step page
  // stamps its marker via completeOnboardingStepByName when the user finishes it.
  NAME: 'nameStep',
  ECOMMERCE_PLATFORM: 'ecommercePlatformStep', // General users only
  STORE_URL: 'storeUrlStep',
  PRODUCT_CATEGORY: 'productCategoryStep',
  DEMO: 'demoStep',
  // Retained from the old flow — the plan/payment pages already write these.
  PLAN_SELECTION: 'planSelectionStep',
  PAYMENT_COMPLETED: 'paymentCompletedStep',
  REGISTER_COMPLETED: 'registerCompleteStep',
} as const;

/**
 * Onboarding 2.0 registration flow (PRD "YepAI Onboarding Flow 2.0").
 *
 * The ordered source of truth for the post-signup step sequence. A user belongs
 * on the route of their FIRST not-yet-completed step. Shopify and General users
 * share every step except the eCommerce-platform picker: Shopify users already
 * have their platform from the App Store OAuth install, so they skip it.
 *
 *   Shopify:  name → storeUrl → category → plan → payment → demo → done
 *   General:  name → platform → storeUrl → category → plan → payment → demo → done
 *
 * Email / verification / password happen on /auth/register BEFORE this flow.
 */
export interface RegistrationFlowStep {
  /** Marker written into user.stepName when this step is completed. */
  step: string;
  /** Route that collects this step. */
  route: string;
  /** Only General (non-Shopify) users see this step. */
  generalOnly?: boolean;
}

// 4-step signup redesign (Figma 23878-*): the post-signup flow is shortened to just
// two data screens — business (company + categories) then pricing — before "done".
// name / ecommerce-platform / store-url / stripe-payment / demo are dropped; the
// Shopify billing redirect returns to /payment/payment-callback which routes onward.
// PRODUCT_CATEGORY is the marker for the combined business step.
// No '/welcome-onboard' step any more — the merchant drops straight into the
// dashboard (/home) once the plan step completes. REGISTER_COMPLETED is still marked
// (in PlanDetailsPage's Start-Free completion), which short-circuits this resolver to
// null so the guard never pulls a finished user back into the funnel.
export const REGISTRATION_FLOW_2_0: RegistrationFlowStep[] = [
  { step: PRE_ONBOARDING_STEPS.PRODUCT_CATEGORY, route: '/business' },
  { step: PRE_ONBOARDING_STEPS.PLAN_SELECTION, route: '/plan-details' },
];

/**
 * Pre-onboarding step progression order
 */
export const PRE_ONBOARDING_FLOW = [
  PRE_ONBOARDING_STEPS.QUESTIONNAIRE,
  PRE_ONBOARDING_STEPS.CONFIGURE_AVATAR,
  PRE_ONBOARDING_STEPS.PLAN_SELECTION,
  PRE_ONBOARDING_STEPS.PAYMENT_COMPLETED,
] as const;

// ============================================
// ONBOARDING FLOW (Post-registration setup)
// ============================================

/**
 * Onboarding step IDs for the main onboarding process
 */
export const ONBOARDING_STEPS = {
  // Common steps
  SUPPORT_EMAIL: 'support-email',
  COMPANY_LOGO: 'company-logo',
  UPLOAD_FAQS: 'upload-faqs',

  // Shopify-specific steps
  CONNECT_STORE: 'connect-store',
  GO_LIVE: 'go-live',

  // Non-Shopify specific steps
  CUSTOMISE_AGENT: 'customise-agent',
  MANUAL_INSTALL: 'manual-install',

  // System steps
  IN_DASHBOARD: 'indashboard',
} as const;

/**
 * Final onboarding steps that indicate completion
 */
export const FINAL_ONBOARDING_STEPS = [
  ONBOARDING_STEPS.GO_LIVE,
  ONBOARDING_STEPS.MANUAL_INSTALL,
] as const;

// ============================================
// ONBOARDING STEP DEFINITIONS
// ============================================

export interface OnboardingStepDefinition {
  id: string;
  title: string;
  description: string;
  stepNumber: number;
  isRequired: boolean;
  component: string;
  status: 'locked' | 'current' | 'completed';
}

/**
 * Shopify user onboarding flow (5 steps)
 */
export const SHOPIFY_ONBOARDING_STEPS: Omit<OnboardingStepDefinition, 'status'>[] = [
  {
    id: ONBOARDING_STEPS.CONNECT_STORE,
    title: 'Connect to your e-commerce store',
    description: 'Your Shopify store has been successfully connected to YepAI',
    stepNumber: 1,
    isRequired: true,
    component: 'ShopifyConnectedStep',
  },
  {
    id: ONBOARDING_STEPS.SUPPORT_EMAIL,
    title: 'Connect your support email',
    description:
      'Please provide a support email address for any inquiries or assistance you may need.',
    stepNumber: 2,
    isRequired: true,
    component: 'SupportEmailStep',
  },
  {
    id: ONBOARDING_STEPS.COMPANY_LOGO,
    title: 'Add your logo or image',
    description:
      'Upload your company logo or a personal image to create a more personalized look for your visitors.',
    stepNumber: 3,
    isRequired: false,
    component: 'CompanyLogoStep',
  },
  {
    id: ONBOARDING_STEPS.UPLOAD_FAQS,
    title: 'Upload your help center FAQs',
    description:
      "Please upload all the documents you'd like to include for training our AI chatbot.",
    stepNumber: 4,
    isRequired: false,
    component: 'UploadFAQsStep',
  },
  {
    id: ONBOARDING_STEPS.GO_LIVE,
    title: 'GO LIVE! One click installation',
    description:
      'Configure your AI chatbot in how it should greet customers, how it should look and sound like.',
    stepNumber: 5,
    isRequired: true,
    component: 'GoLiveStep',
  },
];

/**
 * Non-Shopify user onboarding flow (5 steps)
 */
export const NON_SHOPIFY_ONBOARDING_STEPS: Omit<OnboardingStepDefinition, 'status'>[] = [
  {
    id: ONBOARDING_STEPS.SUPPORT_EMAIL,
    title: 'Connect your support email',
    description:
      'Please provide a support email address for any inquiries or assistance you may need.',
    stepNumber: 1,
    isRequired: true,
    component: 'SupportEmailStep',
  },
  {
    id: ONBOARDING_STEPS.COMPANY_LOGO,
    title: 'Add your logo or image',
    description:
      'Upload your company logo or a personal image to create a more personalized look for your visitors.',
    stepNumber: 2,
    isRequired: false,
    component: 'CompanyLogoStep',
  },
  {
    id: ONBOARDING_STEPS.UPLOAD_FAQS,
    title: 'Upload your help center FAQs',
    description:
      "Please upload all the documents you'd like to include for training our AI chatbot.",
    stepNumber: 3,
    isRequired: true,
    component: 'UploadFAQsStep',
  },
  {
    id: ONBOARDING_STEPS.CUSTOMISE_AGENT,
    title: 'Customise your AI Agent',
    description: "Configure your AI assistant's appearance and behavior",
    stepNumber: 4,
    isRequired: true,
    component: 'CustomiseAgentStep',
  },
  {
    id: ONBOARDING_STEPS.MANUAL_INSTALL,
    title: 'Install chat widget: Manual install',
    description:
      'Install the chat widget on your website by copying and pasting the provided code snippet.',
    stepNumber: 5,
    isRequired: true,
    component: 'ManualInstallStep',
  },
];

// ============================================
// USER STATE CONSTANTS
// ============================================

/**
 * User state types for routing and flow control
 */
export const USER_STATE = {
  NEW_USER: 'new_user',
  NEW_USER_WITH_SHOPIFY: 'new_user_with_shopify',
  QUESTIONNAIRE_STEP: 'questionnaire_step',
  PLAN_SELECTION_STEP: 'plan_selection_step',
  SHOPIFY_CONFIGURATION_REQUIRED: 'shopify_configuration_required',
  NON_SHOPIFY_CONFIGURATION_REQUIRED: 'non_shopify_configuration_required',
  COMPLETED_GUIDE: 'completed_guide',
  COMPLETED_GUIDE_PENDING_PRODUCT_SCAN: 'completed_guide_pending_product_scan',
  PAID_USER_COMPLETED: 'paid_user_completed',
  REGISTER_COMPLETED: 'register_completed',
} as const;

export type UserStateType = (typeof USER_STATE)[keyof typeof USER_STATE];

// ============================================
// ROUTES CONFIGURATION
// ============================================

/**
 * Required routes for each user state
 */
export const USER_STATE_ROUTES: Record<UserStateType, string> = {
  [USER_STATE.NEW_USER]: '/platform-selection',
  [USER_STATE.NEW_USER_WITH_SHOPIFY]: '/questionnaire',
  [USER_STATE.QUESTIONNAIRE_STEP]: '/configuration-avatar',
  [USER_STATE.PLAN_SELECTION_STEP]: '/plan-details',
  [USER_STATE.SHOPIFY_CONFIGURATION_REQUIRED]: '/configuration-avatar',
  [USER_STATE.NON_SHOPIFY_CONFIGURATION_REQUIRED]: '/configuration-avatar',
  [USER_STATE.COMPLETED_GUIDE]: '/welcome-onboard',
  [USER_STATE.COMPLETED_GUIDE_PENDING_PRODUCT_SCAN]: '/welcome-onboard',
  // Completed users land on Home — the setup guide is reachable from the home
  // rail / Setup Guide nav, no longer a forced /onboarding redirect.
  [USER_STATE.PAID_USER_COMPLETED]: '/home',
  [USER_STATE.REGISTER_COMPLETED]: '/home',
};

/**
 * Routes that bypass normal flow restrictions
 */
export const BYPASS_ROUTES = ['/payment/payment-callback'];

/**
 * Payment flow user states
 */
export const PAYMENT_FLOW_STATES: UserStateType[] = [
  USER_STATE.NEW_USER,
  USER_STATE.NEW_USER_WITH_SHOPIFY,
  USER_STATE.QUESTIONNAIRE_STEP,
  USER_STATE.PLAN_SELECTION_STEP,
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Parse step names from comma-separated string
 */
export function parseStepNames(stepName?: string): string[] {
  if (!stepName || stepName.trim() === '') {
    return [];
  }
  return stepName
    .split(',')
    .map(name => name.trim())
    .filter(Boolean);
}

/**
 * Check if user has completed specific steps
 */
export function hasCompletedSteps(userStepName: string, ...requiredSteps: string[]): boolean {
  const completedSteps = parseStepNames(userStepName);
  return requiredSteps.every(step => completedSteps.includes(step));
}

/**
 * Check if user has completed a specific step
 */
export function hasCompletedStep(userStepName: string, stepName: string): boolean {
  const completedSteps = parseStepNames(userStepName);
  return completedSteps.includes(stepName);
}

/**
 * Check if user has completed questionnaire and avatar configuration
 */
export function hasCompletedQuestionnaireAndAvatar(userStepName: string): boolean {
  return (
    hasCompletedStep(userStepName, PRE_ONBOARDING_STEPS.QUESTIONNAIRE) &&
    hasCompletedStep(userStepName, PRE_ONBOARDING_STEPS.CONFIGURE_AVATAR)
  );
}

/**
 * Check if user has completed onboarding (reached final steps)
 */
export function hasCompletedOnboarding(stepNames: string[]): boolean {
  return FINAL_ONBOARDING_STEPS.some(finalStep => stepNames.includes(finalStep));
}

/**
 * Check if a step is a final onboarding step
 */
export function isFinalOnboardingStep(stepId: string): boolean {
  return FINAL_ONBOARDING_STEPS.includes(stepId as any);
}

/**
 * Get current user state based on completed steps
 */
export function getUserPreOnboardingState(
  userStepName: string
):
  | 'new'
  | 'questionnaire_only'
  | 'questionnaire_and_avatar'
  | 'plan_selected'
  | 'payment_completed'
  | 'register_completed' {
  const hasQuestionnaire = hasCompletedStep(userStepName, PRE_ONBOARDING_STEPS.QUESTIONNAIRE);
  const hasAvatar = hasCompletedStep(userStepName, PRE_ONBOARDING_STEPS.CONFIGURE_AVATAR);
  const hasPlanSelection = hasCompletedStep(userStepName, PRE_ONBOARDING_STEPS.PLAN_SELECTION);
  const hasPayment = hasCompletedStep(userStepName, PRE_ONBOARDING_STEPS.PAYMENT_COMPLETED);
  const hasRegisterCompleteStep = hasCompletedStep(
    userStepName,
    PRE_ONBOARDING_STEPS.REGISTER_COMPLETED
  );

  if (hasRegisterCompleteStep) return 'register_completed';
  if (hasPayment) return 'payment_completed';
  if (hasPlanSelection) return 'plan_selected';
  if (hasQuestionnaire && hasAvatar) return 'questionnaire_and_avatar';
  if (hasQuestionnaire) return 'questionnaire_only';
  return 'new';
}

// ============================================
// ONBOARDING 2.0 FLOW RESOLVER
// ============================================

/** Every route that belongs to the Onboarding 2.0 registration flow. */
export const REGISTRATION_FLOW_ROUTES: string[] = REGISTRATION_FLOW_2_0.map(s => s.route);

/**
 * Markers that only a PRE-2.0 user can have (the old customer-support flow and
 * its post-registration onboarding). A fresh Onboarding 2.0 signup never writes
 * any of these — so their presence means "existing user, already past
 * registration". Used to short-circuit the 2.0 resolver so we never drag an
 * existing user (whose stepName lacks the new 2.0 markers) back into the funnel.
 */
export const LEGACY_ONBOARDING_MARKERS: string[] = [
  PRE_ONBOARDING_STEPS.QUESTIONNAIRE,
  PRE_ONBOARDING_STEPS.CONFIGURE_AVATAR,
  PRE_ONBOARDING_STEPS.PLATFORM_SELECTION,
  ONBOARDING_STEPS.SUPPORT_EMAIL,
  ONBOARDING_STEPS.COMPANY_LOGO,
  ONBOARDING_STEPS.UPLOAD_FAQS,
  ONBOARDING_STEPS.CONNECT_STORE,
  ONBOARDING_STEPS.GO_LIVE,
  ONBOARDING_STEPS.CUSTOMISE_AGENT,
  ONBOARDING_STEPS.MANUAL_INSTALL,
  ONBOARDING_STEPS.IN_DASHBOARD,
];

/**
 * Resolve where a user belongs in the Onboarding 2.0 flow: the route that
 * collects their FIRST not-yet-completed step. Shopify users skip the
 * general-only eCommerce-platform picker. Returns null when every applicable
 * step is complete — the caller then sends them into the app (/home).
 *
 * `treatPaymentDone` lets the caller mark the payment step satisfied for paid /
 * free-trial users (billing is handled by the existing plan/payment flow, not
 * re-gated here).
 */
export function getNextRegistrationRoute(
  userStepName: string,
  hasShopify: boolean,
  treatPaymentDone = false
): string | null {
  const completed = parseStepNames(userStepName);
  // Anyone already past registration is DONE — never drag them back into
  // onboarding just because an intermediate 2.0 marker is absent. This covers
  // both users who finished the 2.0 flow (REGISTER_COMPLETED) and every existing
  // PRE-2.0 user (any legacy marker), whose stepName predates the new steps.
  if (completed.includes(PRE_ONBOARDING_STEPS.REGISTER_COMPLETED)) return null;
  if (completed.some(step => LEGACY_ONBOARDING_MARKERS.includes(step))) return null;
  // Payment done implies the plan was chosen (you can't pay without selecting a
  // plan). Treat BOTH the plan and payment steps as satisfied so a paid user is
  // never bounced back to /plan-details even when planSelectionStep wasn't
  // recorded — that would loop against the /plan-details paid-user guard.
  const paymentDone = treatPaymentDone || completed.includes(PRE_ONBOARDING_STEPS.PAYMENT_COMPLETED);
  for (const { step, route, generalOnly } of REGISTRATION_FLOW_2_0) {
    if (generalOnly && hasShopify) continue;
    if (step === PRE_ONBOARDING_STEPS.PLAN_SELECTION && paymentDone) continue;
    if (step === PRE_ONBOARDING_STEPS.PAYMENT_COMPLETED && paymentDone) continue;
    if (!completed.includes(step)) return route;
  }
  return null;
}

/**
 * The ordered routes that actually apply to THIS user — the full 2.0 flow minus
 * the steps they skip (general-only eCommerce-platform picker for Shopify users;
 * the payment step for paid / free-trial users). Used for backward navigation
 * and for deciding whether a route is a completed earlier step.
 */
export function getApplicableFlowRoutes(hasShopify: boolean, treatPaymentDone = false): string[] {
  return REGISTRATION_FLOW_2_0.filter(({ step, generalOnly }) => {
    if (generalOnly && hasShopify) return false;
    if (step === PRE_ONBOARDING_STEPS.PAYMENT_COMPLETED && treatPaymentDone) return false;
    return true;
  }).map(s => s.route);
}

/**
 * The route of the step BEFORE `currentRoute` in this user's applicable flow, or
 * null when `currentRoute` is the first applicable step (or not part of the
 * flow). Powers the onboarding Back button so it lands on the real previous
 * step instead of walking browser history (which `advance` replaces).
 */
export function getPreviousRegistrationRoute(
  currentRoute: string,
  hasShopify: boolean,
  treatPaymentDone = false
): string | null {
  const routes = getApplicableFlowRoutes(hasShopify, treatPaymentDone);
  const idx = routes.indexOf(currentRoute);
  return idx > 0 ? routes[idx - 1] : null;
}
